import { ServerConfigService } from 'src/app/share/service/server-config.service';
import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject, throwError, Subject, timer, Subscription, pipe } from 'rxjs';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../../web-generator/generator-env-token';
import { HttpErrorResponse } from '@angular/common/http';
import { delay, filter, switchMap, tap } from 'rxjs/operators';
import { BackStagePayload } from '../../web-generator/template-generator/entity/back-stage-payload';
import { OAuthError } from 'src/app/share/entity/OauthError';
import { GlobalComponentService, ReusableInfoAlertComponent } from '@techmore/global';
import { TranslateService } from '@ngx-translate/core';
import { AbstractTemplateService } from '../../web-generator/template-generator/entity/abstract-template-service';
import { ITemplate } from '../../web-generator/template-generator/entity/i-template';
import { parseJWTtoken } from 'src/app/util';
import * as moment from 'moment';

/** 模板類型 */
export enum TemplateType {
  /** 首頁模板 */
  index,
  /** 主題模板 */
  subject,
}

/** 後台模板編輯服務代理 */
@Injectable({
  providedIn: 'root',
})
export class BackStageTemplateService {
  /** @deprecated 是否正在刷新中 */
  private _isRefreshing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** 是否正在刷新token中 */
  private _isRefreshingToken = false;
  /** 是否正在刷新token中 */
  public get isRefreshingToken() { return this._isRefreshingToken; }
  /** 刷新後的token推播 */
  private _tokenRefreshed$ = new Subject<void>();
  /** 模板服務 */
  templateService: AbstractTemplateService<ITemplate>;
  /** 模板類型 */
  type: TemplateType;
  /** 最後一次操作畫面的時間戳記 */
  private lastedActiveTimeStamp: number;
  /** 超時警告計時器訂閱 */
  private timeoutAlertTimerSubc: Subscription;
  /** 超時警告計時器訂閱 */
  private timeoutAlert2TimerSubc: Subscription;
  /** 超時計時器訂閱 */
  private timeoutTimerSubc: Subscription;
  /** 後台首頁編輯溝通 url */
  private _stageUrl: string;

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
    private globalComponentService: GlobalComponentService,
    private translateService: TranslateService,
    private serverConfigService: ServerConfigService,
  ) {}

  setStageUrl(stageUrl: string) {
    this._stageUrl = stageUrl;
  }

  listenOnWindowMessage(isIndexPage: boolean, isSubjectDetailPage: boolean): Subject<BackStagePayload> {
    if (isIndexPage) {
      this.type = TemplateType.index;
    } else if (isSubjectDetailPage) {
      this.type = TemplateType.subject;
    }

    const subj = new Subject<BackStagePayload>();
    window.addEventListener('message', (event) => {
      const match = this.generatorEnv.allowBackStageDomain.find(domain => domain === event.origin);
      if (!match) { return; }

      const dataFromParent = event.data;
      if (dataFromParent) {
        if (this.type === TemplateType.index) { // 首頁
          if (dataFromParent.indexEdit && dataFromParent.indexEdit.accessToken && dataFromParent.indexEdit.refreshToken) {
            // 首頁編輯
            sessionStorage.setItem(this.generatorEnv.accessTokenKey, dataFromParent.indexEdit.accessToken);
            sessionStorage.setItem(this.generatorEnv.refreshTokenKey, dataFromParent.indexEdit.refreshToken);
            this.startReadyToTimeOutTimer(dataFromParent.indexEdit.accessToken, dataFromParent.indexEdit.refreshToken);
            subj.next(dataFromParent.indexEdit);
            if (this._isRefreshingToken) { this._isRefreshingToken = false; this._tokenRefreshed$.next(); }
            if (!this.serverConfigService.isEditModeUrl) { this.serverConfigService.isEditModeUrl = true; }
          } else if (dataFromParent === 'close') {
            window.close();
          } else if (dataFromParent === 'timeoutRefreshFail') {
            this.globalComponentService.openErrorAlert('連線超時，請重新登入', {
              afterClose: () => {
                window.close();
              }
            });
          }
        } else if (this.type === TemplateType.subject) { // 主題頁
          if (dataFromParent.subjectEdit && dataFromParent.subjectEdit.accessToken) {
            // 主題編輯
            sessionStorage.setItem(this.generatorEnv.accessTokenKey, dataFromParent.subjectEdit.accessToken);
            sessionStorage.setItem(this.generatorEnv.refreshTokenKey, dataFromParent.subjectEdit.refreshToken);
            this.startReadyToTimeOutTimer(dataFromParent.subjectEdit.accessToken, dataFromParent.subjectEdit.refreshToken);
            subj.next(dataFromParent.subjectEdit);

            if (this._isRefreshingToken) {
              this._isRefreshingToken = false;
              this._tokenRefreshed$.next();
            } else {
              if (!this.serverConfigService.isEditModeUrl) { this.serverConfigService.isEditModeUrl = true; }
            }
          } else if (dataFromParent === 'close') {
            window.close();
          } else if (dataFromParent === 'timeoutRefreshFail') {
            this.globalComponentService.openErrorAlert('連線超時，請重新登入', {
              afterClose: () => {
                window.close();
              }
            });
          }
        } else { // 未定意的位址
          console.error('未匹配路由的網址');
        }
      }
    }, false);
    return subj;
  }

  /** 註冊模板服務實例 */
  registTemplateService(service: AbstractTemplateService<ITemplate>, type: TemplateType) {
    if (type === this.type) {
      this.templateService = service;
      service._errorHandler = this._errorHandler;
    } else {
      service._errorHandler = this._errorHandler; // 再首頁調用主題的API會因為沒有錯誤處理，發生401時不會重取，先這樣處理
    }
  }

  // /** 提交首頁編輯模板設定 */
  // updateTemplate(): Observable<Template> {
  //   return this.templateService.updateTemplate().pipe(
  //     catchError((error, observable) => this._errorHandler<any>(error, observable)),
  //   );
  // }

  // /** 移動區塊順序 */
  // moveSection(uuid: string, direction: boolean) {
  //   this.templateService.moveSection(uuid, direction);
  // }

  private _errorHandler = <T extends unknown>(error: HttpErrorResponse, observableFunc: (args?: any) => Observable<T>): Observable<any> => {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        switch (error.error.subStatus) {
          case '612':
            // 登入超時
            if (!this._isRefreshingToken) {
              this._isRefreshingToken = true;
              this.requestUpdateToken();
            }

            return new Observable(observer => {
              const subc = this._tokenRefreshed$.subscribe(() => {
                subc.unsubscribe();
                observer.next();
                observer.complete();
              });
            }).pipe(
              switchMap(() => observableFunc().pipe(delay(500)))
            );
          case '611':
            // 認證失效 請重新登入
            const oauthError: OAuthError = Object.assign(new OAuthError(this.translateService), error.error);
            window['tokenTimeout'] = true;
            this.globalComponentService.openErrorAlert(
              oauthError.getMessage(),
              {
                afterClose: () => {
                  this.requestUpdateToken();
                  window.close();
                },
                showCopyTip: true
              }
            );
            break;
          case '610':
            // token 未經授權
            const oauthError2: OAuthError = Object.assign(new OAuthError(this.translateService), error.error);
            window['tokenTimeout'] = true;
            this.globalComponentService.openErrorAlert(
              oauthError2.getMessage(),
              {
                afterClose: () => {
                  window.close();
                },
              }
            );
            break;
          default:
            break;
        }
      }
    }
    return throwError(error);
  }

  /**
   * 啟動連線超時計時管理
   * 根據 accessToken 和 refreshToken 的過期時間，設定多個計時器來管理超時提醒和自動更新
   * 
   * @param accessToken - 存取權杖，用於解析過期時間
   * @param refreshToken - 刷新權杖，用於解析過期時間
   */
  startReadyToTimeOutTimer(accessToken: string, refreshToken: string) {
    this.lastedActiveTimeStamp = new Date().getTime();
    this.clearAllTimers();

    // 解析 JWT token 獲取過期時間
    const { expTime, refreshExpTime } = this.parseTokenExpireTimes(accessToken, refreshToken);

    // 設定第一次警告：accessToken 快過期時（或根據 refreshToken 計算時間）
    this.setupFirstTimeoutAlert(expTime, refreshExpTime);

    // 設定第二次警告：refreshToken 快過期前一分鐘
    this.setupSecondTimeoutAlert(expTime, refreshExpTime);

    // 設定最終警告：refreshToken 完全過期後
    this.setupFinalTimeoutAlert(expTime, refreshExpTime);
  }

  /**
   * 清除所有已設定的計時器
   * 防止計時器重疊或冗餘執行
   */
  private clearAllTimers(): void {
    if (this.timeoutAlertTimerSubc) {
      this.timeoutAlertTimerSubc.unsubscribe();
      delete this.timeoutAlertTimerSubc;
    }
    if (this.timeoutAlert2TimerSubc) {
      this.timeoutAlert2TimerSubc.unsubscribe();
      delete this.timeoutAlert2TimerSubc;
    }
    if (this.timeoutTimerSubc) {
      this.timeoutTimerSubc.unsubscribe();
      delete this.timeoutTimerSubc;
    }
  }

  /**
   * 解析 accessToken 和 refreshToken 的過期時間
   * 
   * @param accessToken - 存取權杖
   * @param refreshToken - 刷新權杖
   * @returns 包含過期時間戳的物件
   */
  private parseTokenExpireTimes(accessToken: string, refreshToken: string): { expTime: number; refreshExpTime: number } {
    const jwtPayload = parseJWTtoken(accessToken);
    const expTime = jwtPayload['exp'] * 1000;

    const jwtPayload2 = parseJWTtoken(refreshToken);
    const refreshExpTime = jwtPayload2['exp'] * 1000;

    return { expTime, refreshExpTime };
  }

  /**
   * 計算第一次警告的觸發時間
   * 規則：
   * - 預設為 accessToken 過期後加 5 分鐘
   * - 如果 accessToken 和 refreshToken 時間差超過 2 分鐘，計算兩者的中點時間
   * 
   * @param expTime - accessToken 過期時間戳
   * @param refreshExpTime - refreshToken 過期時間戳
   * @returns 警告時間戳
   */
  private calculateFirstAlertTime(expTime: number, refreshExpTime: number): number {
    let alertTime = expTime + 5 * 60 * 1000; // accessToken 過期後加 5 分鐘

    if (refreshExpTime - expTime > 2 * 60 * 1000) {
      // 如果兩個 token 的過期時間差超過 2 分鐘
      if (alertTime > (refreshExpTime - 60000)) {
        // 計算中點時間，作為警告時間
        alertTime = (expTime + refreshExpTime) / 2;
      }
    } else {
      // 時間差不足 2 分鐘，直接使用 accessToken 過期時間
      alertTime = expTime;
    }

    return alertTime;
  }

  /**
   * 設定第一次超時警告計時器
   * 用途：提醒用戶如果有操作則自動更新 token，否則顯示友善提示
   * 
   * @param expTime - accessToken 過期時間戳
   * @param refreshExpTime - refreshToken 過期時間戳
   */
  private setupFirstTimeoutAlert(expTime: number, refreshExpTime: number): void {
    const alertTime = this.calculateFirstAlertTime(expTime, refreshExpTime);
    const alertTimeDiff = alertTime - new Date().getTime();

    if (alertTimeDiff > 0) {
      this.timeoutAlertTimerSubc = timer(alertTimeDiff)
        .subscribe(() => {
          // 檢查最後操作時間是否在 accessToken 過期前 10 分鐘以前
          if (moment(this.lastedActiveTimeStamp).isBefore(moment(expTime - 10 * 60 * 1000))) {
            // 用戶長時間未操作，顯示友善提示
            this.globalComponentService.openSanckBar('還在編輯嗎? 別忘了存檔喔!');
          } else {
            // 用戶近期有操作，自動更新 token 以保持連線
            this.requestUpdateToken();
          }
        });
    }
  }

  /**
   * 設定第二次超時警告計時器
   * 時機：refreshToken 過期前 1 分鐘
   * 用途：強制提醒用戶連線即將斷開，需要盡速存檔，並在 1 分鐘內自動關閉提醒窗口
   * 
   * @param expTime - accessToken 過期時間戳
   * @param refreshExpTime - refreshToken 過期時間戳
   */
  private setupSecondTimeoutAlert(expTime: number, refreshExpTime: number): void {
    const alertTime2 = refreshExpTime - 60000; // refreshToken 過期前 1 分鐘
    const timeOutAlert2Diff = alertTime2 - new Date().getTime();

    if (timeOutAlert2Diff > 0) {
      this.timeoutAlert2TimerSubc = timer(timeOutAlert2Diff)
        .subscribe(() => {
          // 檢查最後操作時間是否在 accessToken 過期前 10 分鐘以前
          if (moment(this.lastedActiveTimeStamp).isBefore(moment(expTime - 10 * 60 * 1000))) {
            // 用戶長時間未操作，顯示警告提醒並設定自動關閉
            this.showConnectionExpiringAlert(60000 - 10000); // 1 分鐘 - 10 秒後自動關閉
          } else {
            // 用戶近期有操作，自動更新 token 以保持連線
            this.requestUpdateToken();
          }
        });
    }
  }

  /**
   * 顯示連線即將過期的警告，並在指定時間後自動關閉
   * 
   * @param autoCloseDuration - 自動關閉的延遲時間（毫秒），預設為 60000 ms（1 分鐘）
   */
  private showConnectionExpiringAlert(autoCloseDuration: number = 60000): void {
    this.globalComponentService.openInfoAlert('連線將於 1 分鐘內到期，請盡速存檔!');

    // 在指定時間後自動關閉提醒窗口
    timer(autoCloseDuration).subscribe(() => {
      try {
        const infoAlertComponent = this.globalComponentService.globalCmp['infoAlert'] as ReusableInfoAlertComponent;
        if (infoAlertComponent && infoAlertComponent.infoConfirm) {
          infoAlertComponent.infoConfirm.close();
        }
      } catch (error) {
        console.warn('關閉 infoAlert 窗口時發生錯誤:', error);
      }
    });
  }

  /**
   * 設定最終超時警告計時器
   * 時機：refreshToken 完全過期後
   * 用途：提醒用戶連線已斷開，需要重新開啟編輯頁面
   * 
   * @param expTime - accessToken 過期時間戳
   * @param refreshExpTime - refreshToken 過期時間戳
   */
  private setupFinalTimeoutAlert(expTime: number, refreshExpTime: number): void {
    const untilExpTimeDiff = refreshExpTime - new Date().getTime();

    if (untilExpTimeDiff > 0) {
      this.timeoutTimerSubc = timer(untilExpTimeDiff)
        .subscribe(() => {
          // 檢查最後操作時間是否在 accessToken 過期前 10 分鐘以前
          if (moment(this.lastedActiveTimeStamp).isBefore(moment(expTime - 10 * 60 * 1000))) {
            // 用戶長時間未操作，關閉舊的警告窗口並顯示新的最終警告
            try {
              const infoAlertComponent = this.globalComponentService.globalCmp['infoAlert'] as ReusableInfoAlertComponent;
              if (infoAlertComponent && infoAlertComponent.infoConfirm) {
                infoAlertComponent.infoConfirm.close();
              }
            } catch (error) {
              console.warn('關閉舊的 infoAlert 窗口時發生錯誤:', error);
            }

            this.globalComponentService.openInfoAlert('連線已斷開，您已經太久沒操作畫面。<br>請重新開啟編輯頁面');
          } else {
            // 用戶近期有操作，自動更新 token 以保持連線
            this.requestUpdateToken();
          }
        });
    }
  }

  /** 要求維持連線 */
  keepConnection() {
    this.lastedActiveTimeStamp = new Date().getTime();
  }

  /** 要求更新存取權杖 */
  requestUpdateToken() {
    window.opener.postMessage('timeoutRefresh', this._stageUrl);
  }
}
